/**
 * AI Assistant Module
 * Handles infrastructure queries and provides intelligent responses
 * Sprint 31: Updated for environment-aware communication
 *
 * @module AIAssistant
 */

// Sprint 36: Environment configuration (proxy only for non-canister services)
import { environment } from '../config/environment';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Device data with metadata
 */
interface DeviceData {
    id: string;
    name: string;
    device_type: string;
    operating_system?: string;
    os?: string;
    ip_address?: string;
    status?: string;
    Location?: string;
    location?: string;
    device_location?: string;
    [key: string]: any;
}

/**
 * Application data tuple format [app_id, metadata]
 */
type ApplicationTuple = [string, ApplicationMetadata];

/**
 * Application metadata
 */
interface ApplicationMetadata {
    name?: string;
    application_type?: string;
    host_device?: string;
    port?: number | string;
    version?: string;
    status?: string;
    [key: string]: any;
}

/**
 * AI response object
 */
interface AIQueryResponse {
    type: 'success' | 'error' | 'info' | 'help';
    response: string;
}

/**
 * AI Assistant class for infrastructure queries
 */
export class AIAssistant {
    private canisterClient: CanisterClient | null = null;
    private deviceData: DeviceData[] = [];
    private applicationData: ApplicationTuple[] = [];
    private initialized: boolean = false;

    /**
     * Initialize the AI Assistant with canister client
     */
    async initialize(canisterClient: CanisterClient): Promise<void> {
        console.log('🔧 AI Assistant starting initialization...');
        if (!canisterClient) {
            console.error('❌ No canister client provided to AI Assistant');
            return;
        }
        this.canisterClient = canisterClient;
        console.log('📡 AI Assistant has canister client, loading data...');
        await this.loadInfrastructureData();
        this.initialized = true;
        console.log('✅ AI Assistant fully initialized with data');
    }

    /**
     * Load device and application data from vector_db
     * Sprint 36: Uses AgentAdapter for both local and mainnet
     */
    private async loadInfrastructureData(): Promise<void> {
        try {
            console.log('Loading infrastructure data...');

            if (!this.canisterClient) {
                console.warn('No canister client available for data loading');
                this.deviceData = [];
                this.applicationData = [];
                return;
            }

            try {
                // Get devices from vector_db via canister client
                const devicesResult = await this.canisterClient.getAllDevices();
                const devicesArray = 'ok' in devicesResult ? devicesResult.ok :
                                    Array.isArray(devicesResult) ? devicesResult : null;

                if (devicesArray && Array.isArray(devicesArray)) {
                    this.deviceData = devicesArray.map(([deviceId, deviceData]: [string, any]) => ({
                        ...deviceData,
                        id: deviceId
                    }));
                    console.log(`AI Assistant loaded ${this.deviceData.length} devices`);
                } else if ('err' in devicesResult) {
                    console.warn('Error loading devices:', devicesResult.err);
                    this.deviceData = [];
                } else {
                    console.warn('Unexpected devices result format:', devicesResult);
                    this.deviceData = [];
                }
            } catch (deviceError) {
                    console.warn('⚠️ Failed to load devices via canister:', deviceError);
                    this.deviceData = [];
                }

                try {
                    // Get applications from vector_db via canister client
                    // Canister returns array directly, not wrapped in {ok: ...}
                    const appsResult = await this.canisterClient.getAllApplications();
                    // Handle both wrapped {ok: ...} format and raw array format
                    const appsArray = 'ok' in appsResult ? appsResult.ok :
                                     Array.isArray(appsResult) ? appsResult : null;

                    if (appsArray && Array.isArray(appsArray)) {
                        this.applicationData = appsArray;
                        console.log(`📱 AI Assistant loaded ${this.applicationData.length} applications via canister`);
                    } else if ('err' in appsResult) {
                        console.warn('⚠️ Error loading applications:', appsResult.err);
                        this.applicationData = [];
                    } else {
                        console.warn('⚠️ Unexpected applications result format:', appsResult);
                        this.applicationData = [];
                    }
                } catch (appError) {
                    console.warn('Failed to load applications via canister:', appError);
                    this.applicationData = [];
                }
        } catch (error) {
            console.error('Failed to load infrastructure data:', error);
            throw error;
        }
    }

    /**
     * Process a natural language query and return relevant infrastructure information
     */
    async processQuery(query: string): Promise<AIQueryResponse> {
        console.log('🤖 AI processQuery called with:', query);
        console.log('🤖 AI initialized status:', this.initialized);
        console.log('🤖 AI device data length:', this.deviceData.length);
        console.log('🤖 AI application data length:', this.applicationData.length);

        if (!this.initialized) {
            console.warn('⚠️ AI Assistant not yet initialized');
            return {
                type: 'error',
                response: 'AI Assistant is still initializing. Please try again in a moment.'
            };
        }

        const lowerQuery = query.toLowerCase();
        console.log('🔍 Processing query:', lowerQuery);

        // Analyze query intent
        if (this.isDeviceQuery(lowerQuery)) {
            return this.handleDeviceQuery(lowerQuery);
        } else if (this.isApplicationQuery(lowerQuery)) {
            return this.handleApplicationQuery(lowerQuery);
        } else if (this.isStatusQuery(lowerQuery)) {
            return this.handleStatusQuery(lowerQuery);
        } else if (this.isCountQuery(lowerQuery)) {
            return this.handleCountQuery(lowerQuery);
        } else if (this.isHelpQuery(lowerQuery)) {
            return this.handleHelpQuery();
        } else {
            return this.handleGeneralQuery(lowerQuery);
        }
    }

    /**
     * Check if query is about devices
     */
    private isDeviceQuery(query: string): boolean {
        const deviceKeywords = ['device', 'server', 'workstation', 'network', 'router', 'switch',
                               'firewall', 'windows', 'linux', 'ubuntu', 'centos', 'instance', 'ec2'];
        return deviceKeywords.some(keyword => query.includes(keyword));
    }

    /**
     * Check if query is about applications
     */
    private isApplicationQuery(query: string): boolean {
        const appKeywords = ['application', 'app', 'service', 'database', 'web', 'api',
                            'monitoring', 'backup', 'nginx', 'apache', 'mysql', 'postgres'];
        return appKeywords.some(keyword => query.includes(keyword));
    }

    /**
     * Check if query is about status
     */
    private isStatusQuery(query: string): boolean {
        return query.includes('status') || query.includes('online') ||
               query.includes('offline') || query.includes('running') ||
               query.includes('down') || query.includes('active');
    }

    /**
     * Check if query is a count query
     */
    private isCountQuery(query: string): boolean {
        return query.includes('how many') || query.includes('count') ||
               query.includes('total') || query.includes('number of');
    }

    /**
     * Check if query is asking for help
     */
    private isHelpQuery(query: string): boolean {
        return query.includes('help') || query.includes('what can') ||
               query.includes('commands') || query === '?';
    }

    /**
     * Handle device-specific queries
     */
    private handleDeviceQuery(query: string): AIQueryResponse {
        console.log('🔍 handleDeviceQuery called with:', query);
        console.log('🔍 Current deviceData:', this.deviceData);

        let devices = [...this.deviceData];
        let description = "all devices";

        console.log('🔍 Starting with devices:', devices.length, 'devices');

        // Filter by location FIRST (most important filter)
        const locationMatch = this.extractLocation(query);
        if (locationMatch) {
            const locationLower = locationMatch.toLowerCase();
            devices = devices.filter(d => {
                // Check both capitalized and lowercase location fields
                const location = (d.Location || d.location || d.device_location || '').toLowerCase();
                return location.includes(locationLower);
            });
            description = `${description} in ${locationMatch}`;
            console.log(`🔍 After location filter (${locationMatch}):`, devices.length, 'devices');
        }

        // Filter by device type
        if (query.includes('server')) {
            devices = devices.filter(d => d.device_type?.toLowerCase() === 'server');
            description = description.replace('all devices', 'servers');
            console.log('🔍 After server filter:', devices.length, 'devices');
        } else if (query.includes('workstation')) {
            devices = devices.filter(d => d.device_type?.toLowerCase() === 'workstation');
            description = description.replace('all devices', 'workstations');
        } else if (query.includes('network')) {
            devices = devices.filter(d => d.device_type?.toLowerCase().includes('network'));
            description = description.replace('all devices', 'network devices');
        }

        // Filter by OS (check both 'operating_system' and 'os' fields)
        if (query.includes('windows')) {
            console.log('🔍 Filtering for Windows...');
            console.log('🔍 Sample device:', devices[0]);

            devices = devices.filter(d => {
                const hasWindows = d.operating_system?.toLowerCase().includes('windows') ||
                                  d.os?.toLowerCase().includes('windows');
                console.log(`🔍 Device ${d.name}: os="${d.os}", operating_system="${d.operating_system}", hasWindows=${hasWindows}`);
                return hasWindows;
            });
            description = "Windows " + description;
            console.log('🔍 After Windows filter:', devices.length, 'devices found');
        } else if (query.includes('linux') || query.includes('ubuntu') || query.includes('centos')) {
            devices = devices.filter(d =>
                d.operating_system?.toLowerCase().includes('linux') ||
                d.operating_system?.toLowerCase().includes('ubuntu') ||
                d.operating_system?.toLowerCase().includes('centos') ||
                d.os?.toLowerCase().includes('linux') ||
                d.os?.toLowerCase().includes('ubuntu') ||
                d.os?.toLowerCase().includes('centos')
            );
            description = "Linux " + description;
        }

        // Filter by status
        if (query.includes('online') || query.includes('active')) {
            devices = devices.filter(d => d.status?.toLowerCase() === 'online' || d.status?.toLowerCase() === 'active');
            description = "online " + description;
        } else if (query.includes('offline') || query.includes('down')) {
            devices = devices.filter(d => d.status?.toLowerCase() === 'offline' || d.status?.toLowerCase() === 'down');
            description = "offline " + description;
        }

        if (devices.length === 0) {
            return {
                type: 'info',
                response: `No ${description} found in the infrastructure.`
            };
        }

        // Format response
        let response = `Found ${devices.length} ${description}:\n\n`;
        devices.forEach(device => {
            const status = device.status || 'Unknown';
            const statusIcon = this.getStatusIcon(status);
            response += `${statusIcon} **${device.name}**\n`;
            response += `   • Type: ${device.device_type || 'Unknown'}\n`;
            response += `   • OS: ${device.operating_system || device.os || 'Unknown'}\n`;
            response += `   • IP: ${device.ip_address || 'N/A'}\n`;
            response += `   • Status: ${status}\n\n`;
        });

        return {
            type: 'success',
            response: response
        };
    }

    /**
     * Handle application-specific queries
     */
    private handleApplicationQuery(query: string): AIQueryResponse {
        let apps = [...this.applicationData];
        let description = "applications";

        // Filter by app type
        if (query.includes('web')) {
            apps = apps.filter(([_, data]) => data.application_type?.toLowerCase().includes('web'));
            description = "web applications";
        } else if (query.includes('database')) {
            apps = apps.filter(([_, data]) => data.application_type?.toLowerCase().includes('database'));
            description = "databases";
        } else if (query.includes('api')) {
            apps = apps.filter(([_, data]) => data.application_type?.toLowerCase().includes('api'));
            description = "APIs";
        }

        // Filter by status
        if (query.includes('running') || query.includes('active')) {
            apps = apps.filter(([_, data]) => data.status?.toLowerCase() === 'running' || data.status?.toLowerCase() === 'active');
            description = "running " + description;
        }

        if (apps.length === 0) {
            return {
                type: 'info',
                response: `No ${description} found in the infrastructure.`
            };
        }

        // Format response
        let response = `Found ${apps.length} ${description}:\n\n`;
        apps.forEach(([id, data]) => {
            const status = data.status || 'Unknown';
            const statusIcon = this.getStatusIcon(status);
            response += `${statusIcon} **${data.name || id}**\n`;
            response += `   • Type: ${data.application_type || 'Unknown'}\n`;
            response += `   • Host: ${data.host_device || 'Unknown'}\n`;
            response += `   • Port: ${data.port || 'N/A'}\n`;
            response += `   • Version: ${data.version || 'Unknown'}\n`;
            response += `   • Status: ${status}\n\n`;
        });

        return {
            type: 'success',
            response: response
        };
    }

    /**
     * Handle status queries
     */
    private handleStatusQuery(_query: string): AIQueryResponse {
        let onlineDevices = this.deviceData.filter(d =>
            d.status?.toLowerCase() === 'online' || d.status?.toLowerCase() === 'active'
        );
        let offlineDevices = this.deviceData.filter(d =>
            d.status?.toLowerCase() === 'offline' || d.status?.toLowerCase() === 'down'
        );
        let unknownDevices = this.deviceData.filter(d =>
            !d.status || (d.status.toLowerCase() !== 'online' &&
                         d.status.toLowerCase() !== 'active' &&
                         d.status.toLowerCase() !== 'offline' &&
                         d.status.toLowerCase() !== 'down')
        );

        let runningApps = this.applicationData.filter(([_, data]) =>
            data.status?.toLowerCase() === 'running' || data.status?.toLowerCase() === 'active'
        );

        let response = "## Infrastructure Status Summary\n\n";
        response += "### Devices\n";
        response += `🟢 Online: ${onlineDevices.length} devices\n`;
        response += `🔴 Offline: ${offlineDevices.length} devices\n`;
        response += `🟡 Unknown: ${unknownDevices.length} devices\n\n`;
        response += "### Applications\n";
        response += `🟢 Running: ${runningApps.length} applications\n`;
        response += `🔴 Stopped: ${this.applicationData.length - runningApps.length} applications\n`;

        return {
            type: 'info',
            response: response
        };
    }

    /**
     * Handle count queries
     */
    private handleCountQuery(_query: string): AIQueryResponse {
        let response = "## Infrastructure Counts\n\n";

        // Device counts by type
        const serverCount = this.deviceData.filter(d => d.device_type?.toLowerCase() === 'server').length;
        const workstationCount = this.deviceData.filter(d => d.device_type?.toLowerCase() === 'workstation').length;
        const networkCount = this.deviceData.filter(d => d.device_type?.toLowerCase().includes('network')).length;

        response += `**Total Devices:** ${this.deviceData.length}\n`;
        response += `• Servers: ${serverCount}\n`;
        response += `• Workstations: ${workstationCount}\n`;
        response += `• Network Devices: ${networkCount}\n\n`;

        response += `**Total Applications:** ${this.applicationData.length}\n`;

        return {
            type: 'info',
            response: response
        };
    }

    /**
     * Handle help queries
     */
    private handleHelpQuery(): AIQueryResponse {
        const response = `## AI Assistant Help

I can help you query and understand your infrastructure. Here are some example questions:

### Device Queries
• "Show me all Windows servers"
• "List Linux devices"
• "What servers are offline?"
• "Show network devices"

### Application Queries
• "What applications are running?"
• "Show all databases"
• "List web services"

### Status Queries
• "What's the infrastructure status?"
• "Show offline devices"
• "What's running?"

### Count Queries
• "How many servers do we have?"
• "Count all devices"
• "Total applications"

### General
• Type any question about your infrastructure
• I'll search through devices and applications to find relevant information`;

        return {
            type: 'help',
            response: response
        };
    }

    /**
     * Handle general queries
     */
    private handleGeneralQuery(query: string): AIQueryResponse {
        // Try to find any matching devices or applications
        const matchingDevices = this.deviceData.filter(d =>
            d.name?.toLowerCase().includes(query) ||
            d.device_type?.toLowerCase().includes(query) ||
            d.operating_system?.toLowerCase().includes(query) ||
            d.os?.toLowerCase().includes(query)
        );

        const matchingApps = this.applicationData.filter(([id, data]) =>
            data.name?.toLowerCase().includes(query) ||
            data.application_type?.toLowerCase().includes(query) ||
            id.toLowerCase().includes(query)
        );

        if (matchingDevices.length === 0 && matchingApps.length === 0) {
            return {
                type: 'info',
                response: `I couldn't find specific information about "${query}". Try asking about:\n• Devices (servers, workstations, network devices)\n• Applications and services\n• Infrastructure status\n\nType "help" for more examples.`
            };
        }

        let response = `Found matches for "${query}":\n\n`;

        if (matchingDevices.length > 0) {
            response += `**Devices (${matchingDevices.length}):**\n`;
            matchingDevices.forEach(d => {
                response += `• ${d.name} (${d.device_type}, ${d.status})\n`;
            });
            response += '\n';
        }

        if (matchingApps.length > 0) {
            response += `**Applications (${matchingApps.length}):**\n`;
            matchingApps.forEach(([id, data]) => {
                response += `• ${data.name || id} (${data.application_type}, ${data.status})\n`;
            });
        }

        return {
            type: 'success',
            response: response
        };
    }

    /**
     * Extract location from natural language query
     */
    private extractLocation(query: string): string | null {
        const queryLower = query.toLowerCase();

        // Common location patterns
        const patterns = [
            /in ([a-z\s]+?)\?/,  // "in Stevenage?"
            /at ([a-z\s]+?)[\s\?]/,  // "at Stevenage"
            /from ([a-z\s]+?)[\s\?]/,  // "from Stevenage"
            /for ([a-z\s]+?)[\s\?]/,  // "for Stevenage"
        ];

        for (const pattern of patterns) {
            const match = queryLower.match(pattern);
            if (match && match[1]) {
                // Capitalize first letter of each word
                return match[1].trim().split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }
        }

        return null;
    }

    /**
     * Get status icon based on status string
     */
    private getStatusIcon(status: string): string {
        const lowerStatus = status?.toLowerCase() || '';
        if (lowerStatus === 'online' || lowerStatus === 'active' || lowerStatus === 'running') {
            return '🟢';
        } else if (lowerStatus === 'offline' || lowerStatus === 'down' || lowerStatus === 'stopped') {
            return '🔴';
        } else {
            return '🟡';
        }
    }

    /**
     * Format markdown-like text to HTML
     */
    formatMarkdown(text: string): string {
        // Convert markdown-like syntax to HTML
        return text
            .replace(/## (.*)/g, '<h3 class="font-semibold text-lg mt-3 mb-2">$1</h3>')
            .replace(/### (.*)/g, '<h4 class="font-medium mt-2 mb-1">$1</h4>')
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/• /g, '<span class="ml-4">• </span>')
            .replace(/\n/g, '<br>');
    }
}

// Create global instance
declare global {
    interface Window {
        aiAssistant: AIAssistant;
    }
}

window.aiAssistant = new AIAssistant();

// Export for module usage
